import { useState, useRef, useEffect, useCallback } from 'react';
import * as Cesium from 'cesium';
import { TRANSFORM_DEFAULT } from '../utils/transformationCodec';

/**
 * Capture the geometry baseline for a tileset.
 * Must be called while the tileset is in its initial, unmodified state.
 */
export function initGeo(tileset) {
  return {
    originalEnu: Cesium.Transforms.eastNorthUpToFixedFrame(tileset.boundingSphere.center),
    initialModelMatrix: Cesium.Matrix4.clone(tileset.modelMatrix),
  };
}

/**
 * Apply a local T/R/S transform (in the ENU frame at the bounding sphere center)
 * on top of the captured initial model matrix.
 *
 *   M_new = E · L · E⁻¹ · M_initial
 *
 * When L = identity → M_new = M_initial (no visual jump on first apply).
 */
export function applyLocalTransform(tileset, geo, vals) {
  if (!tileset || !geo) return;

  const { tx, ty, tz, rx, ry, rz, sx, sy, sz } = vals;
  const { originalEnu, initialModelMatrix } = geo;

  // Build L = Translation * Rotation * Scale
  const L = Cesium.Matrix4.clone(Cesium.Matrix4.IDENTITY);

  Cesium.Matrix4.multiply(
    L,
    Cesium.Matrix4.fromTranslation(new Cesium.Cartesian3(tx, ty, tz), new Cesium.Matrix4()),
    L,
  );

  const hpr = new Cesium.HeadingPitchRoll(
    Cesium.Math.toRadians(rz),
    Cesium.Math.toRadians(ry),
    Cesium.Math.toRadians(rx),
  );
  Cesium.Matrix4.multiply(
    L,
    Cesium.Matrix4.fromRotation(
      Cesium.Matrix3.fromHeadingPitchRoll(hpr, new Cesium.Matrix3()),
      new Cesium.Matrix4(),
    ),
    L,
  );

  Cesium.Matrix4.multiply(
    L,
    Cesium.Matrix4.fromScale(new Cesium.Cartesian3(sx, sy, sz), new Cesium.Matrix4()),
    L,
  );

  // M_new = E · L · E⁻¹ · M_initial
  const E_inv = Cesium.Matrix4.inverse(originalEnu, new Cesium.Matrix4());
  const M = Cesium.Matrix4.multiply(originalEnu, L, new Cesium.Matrix4());
  Cesium.Matrix4.multiply(M, E_inv, M);
  Cesium.Matrix4.multiply(M, initialModelMatrix, M);

  tileset.modelMatrix = M;
}

/**
 * Manages transformation state for point cloud and gaussian splatting tilesets.
 * Lives in BaseCesiumViewer so state persists across TransformationTools panel open/close.
 *
 * @param {object} tileset - Point cloud Cesium tileset
 * @param {object} gaussianTileset - Gaussian splatting Cesium tileset
 * @param {object} initialPointValues - Pre-loaded transform values for point cloud (from Strapi)
 * @param {object} initialGsValues - Pre-loaded transform values for GS (from Strapi)
 */
export function useTransformationTool(tileset, gaussianTileset, initialPointValues, initialGsValues) {
  const [pointValues, setPointValues] = useState(() => initialPointValues ?? { ...TRANSFORM_DEFAULT });
  const [gsValues,    setGsValues]    = useState(() => initialGsValues    ?? { ...TRANSFORM_DEFAULT });

  // Refs for latest committed values (avoid stale closures in handlers)
  const pointValuesRef = useRef(initialPointValues ?? { ...TRANSFORM_DEFAULT });
  const gsValuesRef    = useRef(initialGsValues    ?? { ...TRANSFORM_DEFAULT });

  // Cesium geometry baseline — captured once, never overwritten
  const pointGeoRef = useRef(null);
  const gsGeoRef    = useRef(null);

  useEffect(() => {
    if (tileset && !pointGeoRef.current) {
      pointGeoRef.current = initGeo(tileset);
      // Apply the saved transform immediately after capturing the baseline
      if (initialPointValues) {
        applyLocalTransform(tileset, pointGeoRef.current, initialPointValues);
      }
    }
  }, [tileset, initialPointValues]);

  useEffect(() => {
    if (gaussianTileset && !gsGeoRef.current) {
      gsGeoRef.current = initGeo(gaussianTileset);
      // Apply the saved transform immediately after capturing the baseline
      if (initialGsValues) {
        applyLocalTransform(gaussianTileset, gsGeoRef.current, initialGsValues);
      }
    }
  }, [gaussianTileset, initialGsValues]);

  const applyPoint = useCallback((field, value) => {
    const next = { ...pointValuesRef.current, [field]: value };
    pointValuesRef.current = next;
    setPointValues(next);
    applyLocalTransform(tileset, pointGeoRef.current, next);
  }, [tileset]);

  const applyGs = useCallback((field, value) => {
    const next = { ...gsValuesRef.current, [field]: value };
    gsValuesRef.current = next;
    setGsValues(next);
    applyLocalTransform(gaussianTileset, gsGeoRef.current, next);
  }, [gaussianTileset]);

  return {
    pointValues,
    gsValues,
    pointValuesRef,
    gsValuesRef,
    pointGeoRef,
    gsGeoRef,
    applyPoint,
    applyGs,
  };
}
